import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { getCsrfToken } from 'utils/csrf';

// Dropdown field configuration - maps to backend endpoints
const DROPDOWN_FIELDS = [
  { id: 'job_categories', name: 'Job Category', usedIn: 'Jobs', endpoint: '/hart/job_categories' },
  { id: 'job_types', name: 'Job Type', usedIn: 'Jobs', endpoint: '/hart/job_types' },
  { id: 'release_statuses', name: 'Release Status', usedIn: 'Jobs', endpoint: '/hart/release_statuses' },
  { id: 'job_statuses', name: 'Job Status', usedIn: 'Jobs', endpoint: '/hart/job_statuses' },
  { id: 'facility_statuses', name: 'Facility Status', usedIn: 'Facilities', endpoint: '/hart/facility_statuses' },
  { id: 'vendor_statuses', name: 'Vendor Status', usedIn: 'Vendors', endpoint: '/hart/vendor_statuses' },
  { id: 'client_statuses', name: 'Client Status', usedIn: 'Clients', endpoint: '/hart/client_statuses' },
  { id: 'session_types', name: 'Session Type', usedIn: 'Sessions', endpoint: '/hart/session_types' },
  { id: 'vendor_types', name: 'Vendor Type', usedIn: 'Vendors', endpoint: '/hart/vendor_types' },
  { id: 'industry_sectors', name: 'Industry Sector', usedIn: 'Clients', endpoint: '/hart/industry_sectors' },
  { id: 'geography_types', name: 'Geography Type', usedIn: 'Sessions', endpoint: '/hart/geography_types' },
  { id: 'fg_types', name: 'Focus Group Type', usedIn: 'Sessions', endpoint: '/hart/fg_types' },
  { id: 'survey_modes', name: 'Survey Mode', usedIn: 'Sessions', endpoint: '/hart/survey_modes' },
];

const DropdownManagement = () => {
  const [selectedField, setSelectedField] = useState('');
  const [values, setValues] = useState([]);
  const [newValueInput, setNewValueInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const selectedFieldData = DROPDOWN_FIELDS.find(f => f.id === selectedField);

  // Fetch values when field is selected
  useEffect(() => {
    if (selectedField) {
      fetchValues();
    }
  }, [selectedField]);

  const fetchValues = async () => {
    if (!selectedFieldData) return;
    
    setLoading(true);
    try {
      // Add show_all=true query param for admin panel to see all values including inactive
      const url = `${selectedFieldData.endpoint}?show_all=true`;
      const response = await fetch(url, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch values (${response.status})`);
      }
      
      const data = await response.json();
      setValues(data || []);
    } catch (error) {
      console.error('Error fetching dropdown values:', error);
      alert('Failed to load values. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelect = (fieldId) => {
    setSelectedField(fieldId);
    setNewValueInput('');
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    // Don't immediately clear to avoid flicker
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...values];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    // Save original order for rollback
    const originalOrder = [...values];
    
    // Optimistically update UI
    setValues(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // Update order on backend with transaction-like behavior
    try {
      // Execute all updates and collect promises
      const updatePromises = [];
      for (let i = 0; i < newOrder.length; i++) {
        // Use id for models with auto-incrementing IDs, name for models where name is primary key
        const identifier = newOrder[i].id || encodeURIComponent(newOrder[i].name);
        const promise = fetch(`${selectedFieldData.endpoint}/${identifier}?show_all=true`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ order: i }),
          credentials: 'include',
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to update ${newOrder[i].name}: ${response.statusText}`);
          }
          return response;
        });
        updatePromises.push(promise);
      }
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
    } catch (error) {
      console.error('Error updating order:', error);
      alert(`Failed to update order: ${error.message}. Reverting changes.`);
      // Rollback to original order in UI
      setValues(originalOrder);
      // Refetch to ensure consistency with backend
      await fetchValues();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleAddValue = async () => {
    if (!newValueInput.trim() || !selectedFieldData) return;
    
    try {
      // Calculate the order for the new value (max current order + 1)
      const maxOrder = values.length > 0 ? Math.max(...values.map(v => v.order || 0)) : -1;
      const newOrder = maxOrder + 1;
      
      const response = await fetch(selectedFieldData.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({
          name: newValueInput.trim(),
          order: newOrder,
        }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add value (${response.status})`);
      }
      
      setNewValueInput('');
      await fetchValues();
    } catch (error) {
      console.error('Error adding value:', error);
      alert('Failed to add value. Please try again.');
    }
  };


  const handleDeleteValue = async (value) => {
    // Check usage count before allowing delete
    const usageCount = value.usage_count || 0;
    
    if (usageCount > 0) {
      alert(
        `❌ Cannot delete "${value.name}"\n\n` +
        `${usageCount} record(s) currently use this value.\n\n` +
        `To remove this value:\n` +
        `1. Create a replacement value (if needed)\n` +
        `2. Update all ${usageCount} records to use the new value\n` +
        `3. Then delete this unused value`
      );
      return;
    }
    
    if (!window.confirm(`Delete "${value.name}"?\n\nThis value is not currently in use. Safe to delete.`)) {
      return;
    }
    
    try {
      // Use id for models with auto-incrementing IDs, name for models where name is primary key
      const identifier = value.id || encodeURIComponent(value.name);
      const response = await fetch(`${selectedFieldData.endpoint}/${identifier}?show_all=true`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': getCsrfToken(),
        },
        credentials: 'include',
      });
      
      // 204 No Content is a successful delete
      if (!response.ok && response.status !== 204) {
        const errorText = await response.text();
        console.error('Delete failed:', response.status, errorText);
        throw new Error(`Failed to delete value (${response.status})`);
      }
      
      await fetchValues();
    } catch (error) {
      console.error('Error deleting value:', error);
      alert('Failed to delete value. Please try again.');
    }
  };

  const handleToggleInactive = async (value) => {
    try {
      // Use id for models with auto-incrementing IDs, name for models where name is primary key
      const identifier = value.id || encodeURIComponent(value.name);
      const url = `${selectedFieldData.endpoint}/${identifier}?show_all=true`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({ inactive: !value.inactive }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Toggle failed:', errorText);
        throw new Error(`Failed to toggle visibility (${response.status})`);
      }
      
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Expected JSON but got:', contentType, text.substring(0, 500));
        throw new Error('Server returned HTML instead of JSON');
      }
      
      await response.json();
      await fetchValues();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      alert('Failed to toggle visibility. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Select Dropdown Field</InputLabel>
          <Select
            value={selectedField}
            label="Select field to manage"
            onChange={(e) => handleFieldSelect(e.target.value)}
          >
            {DROPDOWN_FIELDS.map((field) => (
              <MenuItem key={field.id} value={field.id}>
                {field.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedField && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">{selectedFieldData.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Used in: {selectedFieldData.usedIn}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Managing Values
            </Typography>
            <Typography variant="body2">
              • <strong>Hide values:</strong> Click the eye icon to hide values from dropdowns (they remain on existing records)<br />
              • <strong>Delete values:</strong> Only unused values can be deleted. Values with 0 records are safe to remove.<br />
              • <strong>Status fields:</strong> The &quot;Active&quot; value in status dropdowns should not be renamed or removed, as core filtering functionality depends on it.
            </Typography>
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Values ({values.length}):
                </Typography>
              </Box>

              <List sx={{ mb: 2 }}>
                {values.map((value, index) => {
                  const isDragged = draggedIndex === index;
                  const isDragOver = dragOverIndex === index;
                  
                  return (
                    <ListItem
                      key={value.id || value.name}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        opacity: isDragged ? 0.5 : value.inactive ? 0.6 : 1,
                        borderTop: isDragOver && draggedIndex !== null && draggedIndex !== index 
                          ? '3px solid' 
                          : '1px solid',
                        borderTopColor: isDragOver && draggedIndex !== null && draggedIndex !== index 
                          ? 'primary.main' 
                          : 'divider',
                        cursor: 'move',
                        bgcolor: value.inactive ? 'action.hover' : 'transparent',
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            edge="end"
                            aria-label={value.inactive ? 'show' : 'hide'}
                            onClick={() => handleToggleInactive(value)}
                            title={value.inactive ? 'Show in dropdowns' : 'Hide from dropdowns'}
                          >
                            {value.inactive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteValue(value)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary', cursor: 'grab' }} />
                      <ListItemText 
                        primary={value.name}
                        secondary={
                          value.inactive 
                            ? `Hidden • ${value.usage_count || 0} record${(value.usage_count || 0) === 1 ? '' : 's'}` 
                            : `${value.usage_count || 0} record${(value.usage_count || 0) === 1 ? '' : 's'}`
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: value.inactive ? 'text.secondary' : 'text.primary',
                          },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="New Dropdown Value"
                  placeholder="Enter new value"
                  value={newValueInput}
                  onChange={(e) => setNewValueInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddValue()}
                  size="small"
                  InputLabelProps={{ 
                    shrink: true,
                    sx: { color: 'primary.main', fontWeight: 600 }
                  }}
                  sx={{ 
                    flex: 1, 
                    maxWidth: '400px',
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddValue}
                  disabled={!newValueInput.trim()}
                  sx={{ 
                    minWidth: '120px',
                    px: 3,
                    py: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  + Add Value
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default DropdownManagement;
